import React from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useSubscriptions } from '@/features/account/hooks/useSubscriptions'
import subscriptionService from '@/libs/services/subscriptions'
import PricingTable from '@/components/Onboarding/pricing-table'

const { Title } = Typography
const { useToken } = theme

const StyledContainer = styled.div`
  width: 100%;
`

const StyledTitle = styled(Title)`
  text-align: center;
  margin-bottom: ${({ token }) => token.marginLG}px !important;
  color: ${({ token }) => token.colorText};
`

/**
 * SubscriptionPicker component for selecting subscription plans
 * @param {Object} props - Component props
 * @param {Function} props.onTierPurchased - Callback when a tier is purchased
 * @returns {JSX.Element} Rendered subscription picker
 */
const SubscriptionPicker = ({ onTierPurchased }) => {
  const { t } = useTranslation()
  const { token } = useToken()
  const { tiers, cycles, currentSubscription, loading, error, purchaseSubscription } =
    useSubscriptions()

  const handlePurchase = async (tierId, cycleId) => {
    try {
      await purchaseSubscription({ tierId, cycleId })
      if (onTierPurchased) {
        onTierPurchased()
      }
    } catch (error) {
      console.error('Error purchasing subscription:', error)
    }
  }

  const handleCalculatePrice = async (tierId, cycleId) => {
    try {
      const response = await subscriptionService.calculateTierPrice(tierId, cycleId)
      return response.data.totalPrice
    } catch (error) {
      console.error('Error calculating price:', error)
      throw error
    }
  }

  const handleSendPurchaseOrder = async data => {
    try {
      await subscriptionService.sendPurchaseOrder(data)
    } catch (error) {
      console.error('Error sending purchase order:', error)
    }
  }

  if (loading) {
    return <div>{t('common.loading')}</div>
  }

  if (error) {
    return <div>{t('common.error')}</div>
  }

  return (
    <StyledContainer>
      <Row justify="center" align="middle">
        <Col span={24} style={{ marginBottom: token.marginLG }}>
          <StyledTitle level={3} token={token}>
            {t('subscriptions.choosePlanTitle')}
          </StyledTitle>
        </Col>
      </Row>
      <PricingTable
        subscriptionTiers={tiers}
        subscriptionCycles={cycles}
        organizationSubscriptions={currentSubscription ? [currentSubscription] : []}
        calculatePrice={handleCalculatePrice}
        onPurchase={handlePurchase}
        onSend={handleSendPurchaseOrder}
        activeTier={currentSubscription}
      />
    </StyledContainer>
  )
}

SubscriptionPicker.propTypes = {
  onTierPurchased: PropTypes.func,
}

SubscriptionPicker.defaultProps = {
  onTierPurchased: () => {},
}

export default SubscriptionPicker
